<template>
  <div class="max-w-[90vw] mx-auto pb-10">
    <div class="w-full flex justify-end align-center">
      <v-container class="onest-font">
        <a
          @click.prevent="voltar"
          style="color: inherit; cursor: pointer; text-decoration: none"
          class="d-flex align-center d-inline-flex"
        >
          <v-icon size="20" class="mr-1">mdi-arrow-u-left-bottom</v-icon>
          <span style="text-decoration: underline">voltar</span>
        </a>
        <h1 class="font-bold mt-2 text-5xl">Resultados da Funcionalidade</h1>
        <div class="text-lg mt-2 flex flex-col text-gray-600">
          Visualize os dados coletados na validação para a funcionalidade atual.
        </div>
      </v-container>
    </div>

    <v-container>
      <div class="bg-white rounded-lg px-6 py-4 h-full elevation-1">
        <div v-if="loading" class="text-center py-12 text-gray-500 font-medium">
          <v-progress-circular
            indeterminate
            color="primary"
            class="mb-4"
          ></v-progress-circular>
          <p>Carregando detalhes...</p>
        </div>

        <div
          v-else-if="!resultados"
          class="text-center py-12 text-gray-500 border border-dashed border-gray-300 rounded-lg"
        >
          <v-icon
            size="64"
            icon="mdi-emoticon-sad-outline"
            class="mb-4 text-gray-400"
          ></v-icon>
          <h3 class="text-xl font-bold">Erro ao carregar dados</h3>
        </div>

        <div v-else class="pa-0">
          <v-tabs v-model="tab" bg-color="primary" align-tabs="center">
            <v-tab value="resumo">Resumo</v-tab>
            <v-tab value="perguntas">Perguntas (Por Testador)</v-tab>
          </v-tabs>

          <v-window v-model="tab">
            <!-- ABA RESUMO -->
            <v-window-item value="resumo">
              <v-container>
                <div class="mb-6">
                  <h3 class="mb-3">Participantes que Responderam</h3>
                  <div
                    v-if="resultados.testadores.length > 0"
                    class="d-flex flex-col gap-2"
                  >
                    <v-chip
                      v-for="(testador, index) in resultados.testadores"
                      :key="index"
                      color="primary"
                      variant="tonal"
                      class="font-weight-medium"
                    >
                      <v-avatar
                        start
                        color="primary"
                        class="text-white bg-primary mr-1"
                        size="24"
                      >
                        <span class="text-caption">{{
                          testador.nome
                            ? testador.nome.charAt(0).toUpperCase()
                            : '?'
                        }}</span>
                      </v-avatar>
                      {{ testador.nome }}
                    </v-chip>
                  </div>
                  <div v-else class="text-grey">
                    Nenhum participante respondeu ainda.
                  </div>
                </div>

                <v-divider class="my-6"></v-divider>

                <h3 class="mb-4">Critérios Objetivos</h3>
                <v-row>
                  <v-col
                    cols="12"
                    md="6"
                    v-for="criterio in resultados.criteriosObjetivos"
                    :key="criterio.id_criterio"
                  >
                    <v-card variant="outlined" class="pa-4 h-100">
                      <h4 class="mb-2">{{ criterio.descricao }}</h4>
                      <BarChart
                        :data="buildChartData(criterio)"
                        :options="chartOptions"
                      />
                    </v-card>
                  </v-col>
                </v-row>

                <v-divider class="my-6"></v-divider>

                <h3 class="mb-4">Avaliações Subjetivas</h3>
                <div
                  v-for="subj in resultados.avaliacoesSubjetivas"
                  :key="subj.id_avaliacao_subjetiva"
                  class="mb-6"
                >
                  <v-card class="pa-4">
                    <h4 class="mb-3 text-primary">{{ subj.descricao }}</h4>
                    <div v-if="subj.respostas.length === 0" class="text-grey">
                      Nenhuma resposta fornecida.
                    </div>
                    <v-card
                      v-for="(resp, index) in subj.respostas"
                      :key="index"
                      class="mb-3 pa-3"
                      variant="outlined"
                    >
                      <div class="font-weight-bold mb-1">
                        {{ resp.nome_testador }}:
                      </div>
                      <p class="mb-2">{{ resp.resposta }}</p>

                      <div
                        v-if="resp.anexos && resp.anexos.length > 0"
                        class="mt-2"
                      >
                        <div
                          class="font-weight-bold text-caption text-warning mb-2"
                        >
                          <v-icon size="small" class="mr-1"
                            >mdi-paperclip</v-icon
                          >
                          Evidências:
                        </div>
                        <v-row class="mt-1">
                          <v-col
                            cols="12"
                            sm="6"
                            md="4"
                            v-for="anexo in resp.anexos"
                            :key="anexo.id_arquivo"
                          >
                            <v-card
                              variant="outlined"
                              class="overflow-hidden bg-white"
                            >
                              <a
                                v-if="isImage(anexo)"
                                :href="anexo.localizacao"
                                target="_blank"
                                style="text-decoration: none"
                                title="Abrir anexo"
                                class="d-block"
                              >
                                <v-img
                                  :src="anexo.localizacao"
                                  height="180"
                                  cover
                                  class="bg-grey-lighten-2"
                                />
                              </a>
                              <div v-else class="bg-black">
                                <video
                                  :src="anexo.localizacao"
                                  controls
                                  class="w-full"
                                  style="
                                    height: 180px;
                                    width: 100%;
                                    object-fit: contain;
                                  "
                                ></video>
                              </div>
                            </v-card>
                          </v-col>
                        </v-row>
                      </div>
                    </v-card>
                  </v-card>
                </div>
              </v-container>
            </v-window-item>

            <!-- ABA PERGUNTAS (CARROSSEL DE TESTADORES) -->
            <v-window-item value="perguntas">
              <v-container v-if="resultados.testadores.length > 0">
                <div
                  class="d-flex align-center justify-space-between mb-4 pa-2 rounded"
                >
                  <v-btn
                    icon="mdi-chevron-left"
                    @click="prevTestador"
                    :disabled="currentTestadorIndex === 0"
                  ></v-btn>
                  <div class="text-center">
                    <div class="text-h6">{{ currentTestador.nome }}</div>
                    <div class="text-caption text-grey">
                      {{ currentTestador.email }}
                    </div>
                    <v-chip size="x-small" class="mt-1"
                      >Testador {{ currentTestadorIndex + 1 }} de
                      {{ resultados.testadores.length }}</v-chip
                    >
                  </div>
                  <v-btn
                    icon="mdi-chevron-right"
                    @click="nextTestador"
                    :disabled="
                      currentTestadorIndex === resultados.testadores.length - 1
                    "
                  ></v-btn>
                </div>

                <!-- CÓPIA DO FORMULÁRIO -->
                <v-card variant="outlined" class="pa-6">
                  <h3 class="mb-4 text-primary">Formulário de Validação</h3>

                  <!-- Objetivas (Cópia visual do formulário) -->
                  <div
                    v-for="pergunta in resultados.perguntasFormulario.objetivas"
                    :key="pergunta.id_criterio_objetivo"
                    class="mb-6"
                  >
                    <div class="font-weight-bold mb-2">
                      {{ pergunta.descricao }}
                    </div>
                    <v-radio-group
                      :model-value="
                        getRespostaObjetivaUsuario(
                          pergunta.id_criterio_objetivo,
                        )
                      "
                      readonly
                      inline
                    >
                      <v-radio
                        v-for="opcao in pergunta.opcoes"
                        :key="opcao.id_opcao_criterio"
                        :label="opcao.descricao"
                        :value="opcao.id_opcao_criterio"
                      ></v-radio>
                    </v-radio-group>
                  </div>

                  <v-divider class="my-4"></v-divider>

                  <!-- Subjetivas (Cópia visual do formulário) -->
                  <div
                    v-for="pergunta in resultados.perguntasFormulario
                      .subjetivas"
                    :key="pergunta.id_avaliacao_subjetiva"
                    class="mb-6"
                  >
                    <div class="font-weight-bold mb-2">
                      {{ pergunta.descricao_para_avaliacao }}
                    </div>
                    <v-textarea
                      :model-value="
                        getRespostaSubjetivaUsuario(
                          pergunta.id_avaliacao_subjetiva,
                        )
                      "
                      readonly
                      variant="outlined"
                      auto-grow
                      rows="2"
                      bg-color="grey-lighten-4"
                    ></v-textarea>
                  </div>

                  <v-divider class="my-4"></v-divider>

                  <!-- Anexos Gerais da Funcionalidade preenchidos pelo usuário -->
                  <div v-if="getAnexosUsuario().length > 0" class="mb-4">
                    <h4 class="mb-3 text-warning">
                      <v-icon>mdi-paperclip</v-icon> Evidências / Bugs
                      Reportados
                    </h4>
                    <v-row>
                      <v-col
                        cols="12"
                        sm="6"
                        md="4"
                        v-for="anexo in getAnexosUsuario()"
                        :key="anexo.id_arquivo"
                      >
                        <v-card
                          variant="outlined"
                          class="h-100 rounded-lg overflow-hidden border-slate-300"
                        >
                          <a
                            v-if="isImage(anexo)"
                            :href="anexo.localizacao"
                            target="_blank"
                            style="text-decoration: none"
                            class="d-block"
                          >
                            <v-img
                              :src="anexo.localizacao"
                              height="200"
                              cover
                              class="bg-grey-lighten-3"
                            />
                          </a>
                          <div v-else class="bg-black">
                            <video
                              :src="anexo.localizacao"
                              controls
                              class="w-full"
                              style="
                                height: 200px;
                                width: 100%;
                                object-fit: contain;
                              "
                            ></video>
                          </div>
                          <div class="pa-3 bg-slate-50">
                            <div
                              class="text-caption text-truncate font-weight-medium"
                              :title="anexo.nome"
                            >
                              {{ anexo.nome }}
                            </div>
                            <div
                              class="text-caption text-grey text-truncate mt-1"
                            >
                              {{ anexo.descricao_bug }}
                            </div>
                          </div>
                        </v-card>
                      </v-col>
                    </v-row>
                  </div>
                </v-card>
              </v-container>
              <v-container v-else class="text-center pa-6">
                <v-icon
                  size="64"
                  color="grey"
                  icon="mdi-account-cancel-outline"
                ></v-icon>
                <p class="mt-2 text-grey">
                  Nenhum testador enviou respostas para esta funcionalidade.
                </p>
              </v-container>
            </v-window-item>
          </v-window>
        </div>
      </div>
    </v-container>
  </div>
</template>

<script>
/**
 * @module ResultadosValidacaoFuncionalidade
 * @desc Exibe as guias Resumo e Perguntas (por testador) da funcionalidade validada.
 */
import axios from 'axios';
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
} from 'chart.js';
import { Bar } from 'vue-chartjs';

import store from '@/store/store';

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
);

export default {
  name: 'ResultadosValidacaoFuncionalidade',
  components: {
    BarChart: Bar,
  },
  /**
   * @returns {Object} Dados reativos do componente
   */
  data() {
    return {
      idProjeto: null,
      idFormularioEscopo: null,
      loading: true,
      resultados: null,
      tab: 'resumo',
      currentTestadorIndex: 0,
      chartOptions: {
        responsive: true,
        plugins: {
          legend: { display: false },
        },
        scales: {
          y: {
            beginAtZero: true,
            max: 100,
            ticks: {
              callback: function (value) {
                return value + '%';
              },
            },
          },
        },
      },
    };
  },
  computed: {
    /**
     * Retorna o testador sendo visualizado atualmente no carrossel.
     * @returns {Object|null}
     */
    currentTestador() {
      if (this.resultados && this.resultados.testadores.length > 0) {
        return this.resultados.testadores[this.currentTestadorIndex];
      }

      return null;
    },
    /**
     * Retorna o objeto completo de respostas individuais do testador atual.
     * @returns {Object|null}
     */
    currentRespostasIndividuais() {
      if (this.currentTestador && this.resultados) {
        return this.resultados.respostasIndividuais.find(
          (r) => r.id_usuario === this.currentTestador.id_usuario,
        );
      }

      return null;
    },
  },
  created() {
    this.idProjeto = this.$route.params.idProjeto;
    this.idFormularioEscopo = this.$route.params.idFormularioEscopo;
    this.carregarResultados();
  },
  methods: {
    /**
     * Busca no backend os dados detalhados para a funcionalidade selecionada.
     */
    async carregarResultados() {
      this.loading = true;
      try {
        const apiUrl = process.env.VUE_APP_API_URL;
        const response = await axios.get(
          `${apiUrl}/api/v1/validacoes/formulario-escopo/${this.idFormularioEscopo}/resultados`,
          {
            headers: { Authorization: `Bearer ${store.state.token}` },
          },
        );
        this.resultados = response.data;
      } catch (error) {
        console.error('Erro ao carregar resultados da funcionalidade:', error);
      } finally {
        this.loading = false;
      }
    },
    /**
     * Navega de volta para a lista de roteiros.
     */
    voltar() {
      this.$router.push({
        path: `/projects/${this.idProjeto}/resultados-roteiros`,
      });
    },
    /**
     * Constrói os dados do gráfico para um critério.
     * @param {Object} criterio
     * @returns {Object} Dados formatados para o vue-chartjs
     */
    buildChartData(criterio) {
      // Ordenar por pontuacao para exibir do menor para o maior se desejar
      const labels = criterio.opcoes.map(
        (op) => `${op.pontuacao} - ${op.descricao}`,
      );
      const data = criterio.opcoes.map((op) => op.porcentagem);

      return {
        labels,
        datasets: [
          {
            label: '% Votos',
            backgroundColor: '#1976D2',
            data,
          },
        ],
      };
    },
    /**
     * Verifica se o anexo é uma imagem
     * @param {Object} anexo
     * @returns {boolean}
     */
    isImage(anexo) {
      if (anexo.tipo_arquivo && anexo.tipo_arquivo.toUpperCase() === 'IMAGEM') {
        return true;
      }
      if (anexo.nome) {
        return /\.(jpg|jpeg|png|gif|webp)$/i.test(anexo.nome);
      }

      return false;
    },
    /**
     * Avança para o próximo testador no carrossel.
     */
    nextTestador() {
      if (this.currentTestadorIndex < this.resultados.testadores.length - 1) {
        this.currentTestadorIndex++;
      }
    },
    /**
     * Volta para o testador anterior no carrossel.
     */
    prevTestador() {
      if (this.currentTestadorIndex > 0) {
        this.currentTestadorIndex--;
      }
    },
    /**
     * Retorna o ID da opção objetiva que o usuário atual escolheu para essa pergunta.
     * @param {number} idCriterio
     * @returns {number|null}
     */
    getRespostaObjetivaUsuario(idCriterio) {
      if (!this.currentRespostasIndividuais) {
        return null;
      }
      const resp = this.currentRespostasIndividuais.respostasObjetivas.find(
        (r) => r.id_criterio === idCriterio,
      );

      return resp ? resp.id_opcaoEscolhida : null;
    },
    /**
     * Retorna o texto subjetivo que o usuário atual preencheu para essa pergunta.
     * @param {number} idAvaliacaoSubj
     * @returns {string}
     */
    getRespostaSubjetivaUsuario(idAvaliacaoSubj) {
      if (!this.currentRespostasIndividuais) {
        return '';
      }
      const resp = this.currentRespostasIndividuais.respostasSubjetivas.find(
        (r) => r.id_avaliacao_subjetiva === idAvaliacaoSubj,
      );

      return resp ? resp.resposta : '';
    },
    /**
     * Retorna a lista de anexos/bugs enviados pelo usuário atual.
     * @returns {Array}
     */
    getAnexosUsuario() {
      if (!this.currentRespostasIndividuais) {
        return [];
      }

      return this.currentRespostasIndividuais.anexos;
    },
  },
};
</script>
